import { Observer } from 'mobx-react'
import { arrayOf, bool, func, node, oneOfType, shape, string } from 'prop-types'
import { Helmet } from 'react-helmet-async'
import { NavLink, Route, Switch, Redirect } from 'react-router-dom'
import { Loading } from '@winnin/winnin_ui.components.loading'

import { useTranslate } from 'lib/tolgee'
import TOLGEE_GLOBALS from 'utils/tolgeeGlobals'

import { ReactComponent as Dots } from '../../assets/images/svg/dots-yellow.svg'
import { ReactComponent as Lock } from '../../assets/images/svg/lock.svg'
import BlockComponents from '../BlockComponents'
import WidthWrapper from '../WidthWrapper'

import './index.scss'

const RouterTabs = ({
  className,
  error,
  handleTabClick,
  loading,
  pageTitle,
  renderHeader,
  rightContent,
  tabs,
  url,
  hideTabs,
  shouldNotAppendSearch,
}) => {
  const t = useTranslate()
  const renderPage = () => {
    if (loading) {
      return <Loading />
    }

    if (error) {
      return <Redirect to="/error/500" />
    }

    return (
      <div className={`TabPage ${className}`}>
        {renderHeader && <div className="tab-header">{renderHeader()}</div>}

        <WidthWrapper>
          {!hideTabs ? (
            <div className="tabs-wrapper">
              <div className="tabs-container">
                {tabs.map(
                  ({
                    path,
                    label,
                    isBlocked,
                    blockMessage = t(TOLGEE_GLOBALS.viewerBlockMessage),
                    isHighlight = false,
                    isFakeBlocked,
                  }) => {
                    const tabUrl = `${url}${path}`
                    const tabUrlWithSearch = `${tabUrl}${window.location.search}`
                    const finalTabUrl = shouldNotAppendSearch ? tabUrl : tabUrlWithSearch
                    return (
                      <BlockComponents isClickBlocked={isBlocked} key={path} blockMessage={blockMessage}>
                        {isBlocked ? (
                          <div className="tab-header-lock">
                            <Lock className="tab-header-lock-icon" />
                            <p className="tab-header-lock-title">{label}</p>
                          </div>
                        ) : (
                          <NavLink
                            key={path}
                            activeClassName="active"
                            className="tab-link"
                            to={finalTabUrl}
                            onClick={() => {
                              handleTabClick?.(label)
                            }}
                          >
                            {isFakeBlocked && <Lock className="fake-header-lock-icon" />}
                            {!!isHighlight && <Dots className="dots" />}
                            {label}
                          </NavLink>
                        )}
                      </BlockComponents>
                    )
                  }
                )}
              </div>

              {rightContent && <div className="tabs-right-content">{rightContent}</div>}
            </div>
          ) : null}

          <div className="tab-content">
            <Switch>
              {/* if no tab is provided, go to first tab */}
              <Route exact path={`${url}`}>
                <Redirect to={`${url}${tabs[0].path}`} />
              </Route>

              {tabs.map(({ path, component, isBlocked }) => {
                if (isBlocked) {
                  return <Redirect key={path} to="/error/unauthorized" />
                }

                return (
                  <Route key={path} path={`${url}${path}`}>
                    {component}
                  </Route>
                )
              })}
            </Switch>
          </div>
        </WidthWrapper>
      </div>
    )
  }

  return (
    <Observer>
      {() => (
        <>
          <Helmet>
            <title>{pageTitle || ''}</title>
          </Helmet>

          {renderPage()}
        </>
      )}
    </Observer>
  )
}

export default RouterTabs

const tabsShape = {
  path: string.isRequired,
  label: oneOfType([string.isRequired, node.isRequired]),
  isBlocked: bool,
  blockMessage: string,
  isHighlight: bool,
}

RouterTabs.propTypes = {
  url: string.isRequired,
  tabs: arrayOf(shape(tabsShape)).isRequired,
  className: string,
  renderHeader: func,
  loading: bool,
  error: bool,
  pageTitle: string,
  handleTabClick: func,
  hideTabs: bool,
}

RouterTabs.defaultProps = {
  className: '',
  renderHeader: null,
  loading: false,
  error: false,
  handleTabClick: null,
  pageTitle: '',
  hideTabs: false,
}
